import React, { useState } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import Eyebrow from '../Eyebrow';
import { marketingColors as C, marketingFonts as F, visualTokens as V } from '../../../theme_landing_page_2';

/**
 * "How we compare" capability table. Deliberately capability-only, not price-vs-price: a "cost
 * at 10 flats" row would read MillOS's own per-flat total against a rival's flat monthly fee
 * and sell against us under our own pricing grid above. Price context stays in the column
 * headers only. Data ported verbatim from the Claude Design Pricing page's `_cmp()` method.
 */

type Mark = 'y' | 'n' | 'p';
interface Cell {
  mark: Mark;
  note?: string;
}
interface Rival {
  key: string;
  name: string;
  price: string;
}
interface Row {
  name: string;
  shortName: string;
  sub: string;
  cells: [Cell, Cell, Cell, Cell, Cell, Cell]; // MillOS, August, Latch, Hammock, Landlord Vision, Landlord Studio
}

const RIVALS: Rival[] = [
  { key: 'august', name: 'August', price: 'Free tier, then ~£14.99/mo' },
  { key: 'latch', name: 'Latch', price: 'Free tier, Pro £20/mo' },
  { key: 'hammock', name: 'Hammock', price: 'From £8/mo (1–3 props)' },
  { key: 'vision', name: 'Landlord Vision', price: 'From £99/year' },
  { key: 'studio', name: 'Landlord Studio', price: 'Free ≤3, then £7.99/mo' },
];

const y = (note?: string): Cell => ({ mark: 'y', note });
const n = (note?: string): Cell => ({ mark: 'n', note });
const pp = (note?: string): Cell => ({ mark: 'p', note });

const ROWS: Row[] = [
  { name: 'Every certificate, watched for you', shortName: 'Certificate tracking', sub: 'Gas Safety, EICR, EPC, PAT, fire risk', cells: [y(), y(), y(), y(), y(), y()] },
  { name: 'MillOS reads the certificate', shortName: 'Reads PDFs', sub: 'Upload a PDF — expiry dates extracted for you', cells: [y(), y('Reads documents'), y(), n('Manual entry'), n('Manual entry'), n('Manual entry')] },
  {
    name: 'Books your statutory renewals before they expire',
    shortName: 'Books renewals',
    sub: 'We spot the expiry, contact the tenant, arrange access and instruct an approved engineer',
    cells: [y('Fully automated'), n('Reminds you, you book'), pp('Routes tenant-reported repairs, not scheduled renewals'), n(), n('Job tracking only'), n()],
  },
  { name: 'Talks to your tenant for you', shortName: 'Tenant contact', sub: 'Access arranged without you relaying messages', cells: [y(), pp('Tenant app, you send'), pp('AI replies to enquiries'), n(), n(), pp('Messaging, you send')] },
  { name: 'Portfolio compliance score', shortName: 'Compliance score', sub: 'One 0–100 number across every property', cells: [y(), pp('Checklist, not a score'), n(), n(), n(), n()] },
  { name: 'MTD for Income Tax', shortName: 'MTD figures', sub: 'Quarterly figures ready for your accountant', cells: [y(), y(), y(), y('Quarterly filing'), y('Quarterly filing'), y('Quarterly filing')] },
  {
    name: 'Sends your figures to your accountant',
    shortName: 'Sends to accountant',
    sub: 'Automatically, every quarter and at year end',
    cells: [y('Sent for you'), pp('You export and send'), pp('You export and send'), pp('You export and send'), pp('Portal, you invite'), pp('You export and send')],
  },
  { name: 'Rent matched to the right flat', shortName: 'Rent matching', sub: 'Arrears flagged automatically', cells: [y(), y(), y(), y(), y(), y()] },
  { name: 'Free plan', shortName: 'Free plan', sub: 'What you get without paying', cells: [y('10 flats · 7 days'), y('Free to start'), y('Unlimited on free tier'), y('1 property'), n(), y('Up to 3 properties')] },
];

const MARK_STYLE: Record<Mark, { symbol: string; bg: string; color: string }> = {
  y: { symbol: '✓', bg: C.millosMint, color: V.tealText },
  n: { symbol: '—', bg: C.panel, color: C.muted },
  p: { symbol: '~', bg: C.tintBlue, color: C.accent },
};

const US_BG = C.tintBlue;

const MillosPricingCompare: React.FC = () => {
  const isMobile = useMediaQuery('(max-width:639px)');
  const [selection, setCmp] = useState<string | null>(null);
  const cmp = isMobile && (!selection || selection === 'all') ? 'august' : selection ?? 'all';

  const shown = cmp === 'all' ? RIVALS : RIVALS.filter((r) => r.key === cmp);
  const keep = shown.map((r) => RIVALS.indexOf(r));
  const cmpCols = isMobile ? 'minmax(0,1.15fr) repeat(2,minmax(0,1fr))' : `236px repeat(${shown.length + 1},minmax(0,1fr))`;
  const cmpMinWidth = isMobile ? 0 : 236 + (shown.length + 1) * 132 + 'px';

  interface Head {
    name: string;
    price: string;
    bg: string;
    nameColor: string;
    priceColor: string;
    weight: number;
    size: string;
  }
  const heads: Head[] = [
    { name: 'MillOS', price: '£6 / flat · Accounting £40 / mo', bg: US_BG, nameColor: C.accent, priceColor: C.millosBlue, weight: 700, size: '16px' },
    ...shown.map((r) => ({ name: r.name, price: r.price, bg: C.white, nameColor: C.navy, priceColor: C.body, weight: 600, size: '14.5px' })),
  ];

  const tabs = [...(isMobile ? [] : [{ key: 'all', label: 'All' }]), ...RIVALS.map((r) => ({ key: r.key, label: r.name }))];

  return (
    <Box component="section" sx={{ padding: { xs: '48px 16px 0', md: '88px 52px 0' } }}>
      <Box sx={{ maxWidth: 1180, margin: '0 auto', position: 'relative', overflow: 'hidden', background: C.mist, border: `1px solid ${C.hairline}`, borderRadius: V.borderRadius32px, padding: { xs: '32px 12px 24px', sm: '40px 20px 32px', md: '56px 44px 48px' } }}>

        <Box sx={{ position: 'relative', maxWidth: '44rem', margin: '0 auto', textAlign: 'center' }}>
          <Eyebrow>How we compare</Eyebrow>
          <Box component="h2" sx={{ fontFamily: F.display, fontWeight: 400, fontSize: 'clamp(2rem,4vw,3.1rem)', lineHeight: 1.12, letterSpacing: '-0.03em' }}>
            Beyond tracking.
            <br />
            <Box component="em" sx={{ fontStyle: 'italic' }}>
              Into action.
            </Box>
          </Box>
          <Box component="p" sx={{ fontSize: { xs: 14, sm: 16.5 }, lineHeight: 1.6, color: C.body, marginTop: '18px' }}>
            MillOS coordinates renewals and prepares your accounts — not just reminders. Choose Certificates, Accounting, or both.
          </Box>
        </Box>

        <Box sx={{ position: 'relative', display: 'flex', gap: '9px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '34px' }}>
          {tabs.map((t) => {
            const active = cmp === t.key;
            return (
              <Box
                key={t.key}
                component="button"
                type="button"
                aria-pressed={active}
                onClick={() => setCmp(t.key)}
                sx={{
                  fontFamily: F.body,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  border: `1px solid ${active ? C.navy : C.hairline}`,
                  background: active ? C.navy : C.white,
                  color: active ? C.white : C.ink,
                  borderRadius: '32px',
                  padding: isMobile ? '9px 12px' : '10px 20px',
                  fontSize: isMobile ? 12 : 14,
                  fontWeight: 600,
                }}
              >
                {t.label}
              </Box>
            );
          })}
        </Box>

        <Box aria-label="Capability comparison" sx={{ position: 'relative', marginTop: '30px', overflowX: 'auto', borderRadius: V.borderRadius20px, background: C.white, border: `1px solid ${C.hairline}` }}>
          <Box sx={{ minWidth: cmpMinWidth }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: cmpCols, borderBottom: `2px solid ${C.accent}` }}>
              <Box sx={{ padding: isMobile ? '16px 8px' : '20px 20px 16px', fontFamily: F.ui, fontSize: isMobile ? 10 : 11, fontWeight: 600, letterSpacing: isMobile ? 0 : '0.1em', textTransform: 'uppercase', color: C.accent, lineHeight: 1.5, alignSelf: 'end' }}>
                Capability
              </Box>
              {heads.map((h, i) => (
                <Box key={i} sx={{ padding: isMobile ? '16px 6px' : '20px 14px 16px', textAlign: 'center', background: h.bg, overflowWrap: 'anywhere' }}>
                  <Box sx={{ fontFamily: F.ui, fontWeight: h.weight, fontSize: isMobile ? 13 : h.size, letterSpacing: '-0.015em', color: h.nameColor, lineHeight: 1.2, minHeight: isMobile ? 0 : 34 }}>{h.name}</Box>
                  <Box sx={{ fontFamily: F.ui, fontSize: isMobile ? 11 : 11.5, fontWeight: 500, color: h.priceColor, lineHeight: 1.45, marginTop: '7px' }}>{h.price}</Box>
                </Box>
              ))}
            </Box>

            {ROWS.map((row) => {
              const cells = [0, ...keep.map((i) => i + 1)].map((ci) => row.cells[ci]);
              return (
                <Box key={row.name} sx={{ display: 'grid', gridTemplateColumns: cmpCols, borderBottom: `1px solid ${C.hairline}` }}>
                  <Box sx={{ padding: isMobile ? '14px 8px' : '16px 20px', borderRight: `1px solid ${C.hairline}`, overflowWrap: 'anywhere' }}>
                    <Box component="span" sx={{ display: 'block', fontFamily: F.ui, fontWeight: 600, fontSize: isMobile ? 12 : 14, lineHeight: 1.35, color: C.navy }}>
                      {isMobile ? row.shortName : row.name}
                    </Box>
                    <Box component="span" sx={{ display: isMobile ? 'none' : 'block', fontSize: 12, color: C.body, marginTop: '4px', lineHeight: 1.45 }}>
                      {row.sub}
                    </Box>
                  </Box>
                  {cells.map((cell, i) => {
                    const s = MARK_STYLE[cell.mark];
                    return (
                      <Box key={i} sx={{ padding: isMobile ? '14px 6px' : '16px 12px', textAlign: 'center', background: i === 0 ? US_BG : C.white, overflowWrap: 'anywhere' }}>
                        <Box
                          component="span"
                          sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', background: s.bg, color: s.color, fontFamily: F.ui, fontSize: 14, fontWeight: 700, lineHeight: 1 }}
                        >
                          {s.symbol}
                        </Box>
                        <Box component="span" sx={{ display: 'block', fontSize: 11.5, color: C.body, marginTop: '7px', lineHeight: 1.45 }}>
                          {cell.note || ''}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '32px', flexWrap: 'wrap' }}>
          <Box
            component={RouterLink}
            to="/register"
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: C.millosMint, color: C.navy, fontWeight: 600, borderRadius: '32px', padding: '14px 26px', fontSize: 16, '&:hover': { background: C.millosBlue, color: C.white } }}
          >
            Try for free today <Box component="span">→</Box>
          </Box>
          <Box
            component={RouterLink}
            to="/book-a-call"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: C.white,
              border: `1px solid ${C.hairline}`,
              color: C.navy,
              borderRadius: '32px',
              padding: '14px 26px',
              fontSize: 16,
              '&:hover': { background: C.millosMint, borderColor: C.millosMint, color: C.navy },
            }}
          >
            Book a call
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MillosPricingCompare;
